import {
  Account,
  Facebook,
  Instagram,
  Twitter,
  UAGLogo,
  Youtube,
} from "@components/icons"
import colors from "@constants/colors"
import { smallcapsBold13Styles } from "@constants/typography"
import { TabletOnly } from "@utils/MediaQuery"
import { uniqueId } from "lodash-es"
import React from "react"
import styled from "styled-components"

import {
  Copyright,
  ExternalFooterLinkComponent,
  Footer,
  FooterLink,
  FooterLinkProps,
  FooterProps,
  LegalGroup,
  LegalLink,
  Logo,
  LogoWrapper,
  Main,
  Social,
  SocialIcon,
  SocialIconWrapper,
  User,
  UserIcon,
} from "./common"

const Column = styled.div`
  display: flex;
  width: calc(3 / 12 * 100vw);
  flex-direction: column;
  flex-grow: 0;
  flex-shrink: 0;
`

const ColumnHeader = styled.a`
  ${smallcapsBold13Styles}
  display: flex;
  height: 54px;
  margin-bottom: 16px;
  align-items: center;
  color: ${colors.offWhite};
`

const ColumnHeaderText = styled.span`
  padding-top: 4px;
`

const ColumnGroupSpacer = styled.div`
  margin-bottom: 66px;
`

const ColumnDivider = styled.div`
  width: calc(0.75 / 12 * 100vw);
`

const Sub = styled.div`
  display: flex;
`

const currentYear = new Date().getFullYear()

const DesktopFooter: React.FC<FooterProps> = ({
  footerBusinessLabelLink,
  footerBusinessLinks,
  footerExploreLabelLink,
  footerExploreLinks,
  footerShopLabelLink,
  footerShopLinks,
  footerSupportLabelLink,
  footerSupportLinks,
  legalLinks,
  socialLinks,
  user,
}) => {
  const renderColumnHeader = (link: FooterLinkProps) => (
    <ColumnHeader>
      <ColumnHeaderText>{link.text}</ColumnHeaderText>
    </ColumnHeader>
  )

  const renderFooterLinks = (links: Array<FooterLinkProps>) =>
    links.length > 0 &&
    links.map((link: FooterLinkProps) => (
      <FooterLink key={uniqueId()} {...link} />
    ))

  const renderLegalLink = (legalUrl, legalText) =>
    legalUrl && (
      <Column>
        <LegalLink to={legalUrl}>{legalText}</LegalLink>
      </Column>
    )

  const renderSocialLink = (
    socialUrl: string | undefined,
    IconComponent: React.ReactNode,
    props?: React.AnchorHTMLAttributes<HTMLAnchorElement>
  ) =>
    socialUrl && (
      <SocialIconWrapper
        href={socialUrl}
        rel="noopener"
        target="_blank"
        {...props}
      >
        <SocialIcon>{IconComponent}</SocialIcon>
      </SocialIconWrapper>
    )

  const { facebook, instagram, twitter, youtube } = socialLinks || {}

  const { limitationsOfLiability, privacyPolicy, termsAndConditions } =
    legalLinks || {}

  return (
    <TabletOnly>
      <Footer>
        <Main>
          <Column>
            <LogoWrapper>
              <Logo to="/">
                <UAGLogo />
              </Logo>
            </LogoWrapper>
          </Column>
          <Column>
            {footerShopLabelLink && renderColumnHeader(footerShopLabelLink)}
            {footerShopLinks && renderFooterLinks(footerShopLinks)}
            <ColumnGroupSpacer />
          </Column>
          <ColumnDivider />
          <Column>
            {footerBusinessLabelLink &&
              renderColumnHeader(footerBusinessLabelLink)}
            {footerBusinessLinks && renderFooterLinks(footerBusinessLinks)}
            <ColumnGroupSpacer />
          </Column>
          <Column />
          <Column>
            {footerExploreLabelLink &&
              renderColumnHeader(footerExploreLabelLink)}
            {footerExploreLinks && renderFooterLinks(footerExploreLinks)}
            <ColumnGroupSpacer />
          </Column>
          <ColumnDivider />
          <Column>
            {footerSupportLabelLink &&
              renderColumnHeader(footerSupportLabelLink)}
            {footerSupportLinks && renderFooterLinks(footerSupportLinks)}
            <ExternalFooterLinkComponent
              className="termly-display-preferences"
              href="#"
              id="tabletTermlyButton"
            >
              <span>Consent Preferences</span>
            </ExternalFooterLinkComponent>
            <ColumnGroupSpacer />
          </Column>
        </Main>
        <Sub>
          <Column>
            <User to="/account">
              <UserIcon>
                <Account />
              </UserIcon>
              {user}
            </User>
          </Column>
          <Social>
            {renderSocialLink(instagram, <Instagram />, {
              "aria-label": "Instagram",
            })}
            {renderSocialLink(twitter, <Twitter />, {
              "aria-label": "Twitter",
            })}
            {renderSocialLink(facebook, <Facebook />, {
              "aria-label": "Facebook",
            })}
            {renderSocialLink(youtube, <Youtube />, {
              "aria-label": "Youtube",
            })}
          </Social>
        </Sub>
        <LegalGroup>
          {renderLegalLink(privacyPolicy, "Privacy Policy")}
          {renderLegalLink(termsAndConditions, "Terms and Conditions")}
          {renderLegalLink(limitationsOfLiability, "Limitations of Liability")}
        </LegalGroup>
        <Copyright>{`© ${currentYear} URBAN ARMOR GEAR, LLC. ALL RIGHTS RESERVED.`}</Copyright>
      </Footer>
    </TabletOnly>
  )
}

export default DesktopFooter
